import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Scroll, 
  Users, 
  UserCheck, 
  Shield, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  Plus, 
  X,
  FileText, 
  Wallet, 
  User, 
  Calendar, 
  Trash2, 
  Edit, 
  Eye, 
  ArrowRight, 
  Loader2,
  Key
} from 'lucide-react';
import { useWills } from '../../hooks/useWills';
import { useAuthStore } from '../../store/authStore';
import { WillDocument, WillWitness, WillBeneficiary } from '../../types/will';
import { verifyWeb3Identity, getAvailableWalletProviders } from '../../lib/picaos';
import Card from '../atoms/Card';
import Button from '../atoms/Button';
import Input from '../atoms/Input';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

const WillWitnessPage: React.FC = () => {
  const { wills, isLoading, error, refetch } = useWills();
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'created' | 'witness' | 'beneficiary'>('created');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showWitnessModal, setShowWitnessModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedWill, setSelectedWill] = useState<WillDocument | null>(null);
  const [isConfirmingWellness, setIsConfirmingWellness] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [confirmationSignature, setConfirmationSignature] = useState('');
  const [isSubmittingConfirmation, setIsSubmittingConfirmation] = useState(false);

  // Filter wills based on active tab
  const filteredWills = wills.filter(will => {
    if (activeTab === 'created') {
      return will.creatorUser?.id === user?.id;
    } else if (activeTab === 'witness') {
      return will.witnesses.some(witness => 
        witness.address === user?.walletAddress || 
        witness.email === user?.email
      );
    } else if (activeTab === 'beneficiary') {
      return will.beneficiaries.some(beneficiary => 
        beneficiary.address === user?.walletAddress || 
        beneficiary.email === user?.email
      );
    }
    return false;
  });

  const handleConfirmWellness = async (will: WillDocument) => {
    setSelectedWill(will);
    setShowWitnessModal(true);
    setIsConfirmingWellness(true);
    setConfirmationMessage(`I, ${user?.name || 'Witness'}, confirm that I am alive and well as of ${new Date().toLocaleDateString()}.`);
  };

  const handleViewWill = (will: WillDocument) => {
    setSelectedWill(will);
    setShowViewModal(true);
  };

  const handleEditWill = (will: WillDocument) => {
    setSelectedWill(will);
    setShowEditModal(true);
  };

  const handleSubmitConfirmation = async () => {
    if (!selectedWill || !user?.walletAddress) {
      toast.error('Missing will or wallet address');
      return;
    }

    setIsSubmittingConfirmation(true);

    try {
      // Find the witness record for the current user
      const witness = selectedWill.witnesses.find(w => 
        w.address === user.walletAddress || w.email === user.email
      );

      if (!witness) {
        throw new Error('You are not a witness for this will');
      }

      // Get wallet providers
      const providers = getAvailableWalletProviders();
      if (providers.length === 0) {
        throw new Error('No wallet providers available. Please install a Web3 wallet.');
      }

      // Use the first available provider
      const provider = providers[0];
      
      // Connect to wallet first to get the address
      toast.loading('Connecting to wallet...', { id: 'wallet-connect' });
      const connectedAddress = await provider.connect();
      
      if (!connectedAddress) {
        throw new Error('Failed to connect to wallet');
      }
      
      // Verify identity with wallet signature
      toast.loading('Verifying your identity...', { id: 'verify-identity' });
      const identity = await verifyWeb3Identity(provider, connectedAddress);
      
      if (!identity.verified) {
        throw new Error('Identity verification failed');
      }
      
      toast.success('Identity verified!', { id: 'verify-identity' });

      // Submit confirmation to database
      toast.loading('Submitting confirmation...', { id: 'submit-confirmation' });
      
      const { error: confirmationError } = await supabase
        .from('witness_confirmations')
        .insert({
          will_id: selectedWill.id,
          witness_address: witness.address,
          confirmation_type: 'wellness_check',
          message: confirmationMessage,
          signature: identity.signature,
          is_valid: true,
          confirmed_at: new Date().toISOString()
        });

      if (confirmationError) {
        throw new Error(`Failed to submit confirmation: ${confirmationError.message}`);
      }

      // Update witness last_confirmation_at
      const { error: updateError } = await supabase
        .from('will_witnesses')
        .update({ 
          last_confirmation_at: new Date().toISOString() 
        })
        .eq('id', witness.id);

      if (updateError) {
        console.error('Failed to update witness confirmation time:', updateError);
        // Non-blocking error
      }

      toast.success('Wellness confirmation submitted successfully!', { id: 'submit-confirmation' });
      
      // Close modal and refresh data
      setShowWitnessModal(false);
      setIsConfirmingWellness(false);
      setConfirmationMessage('');
      setConfirmationSignature('');
      refetch();
      
    } catch (error: any) {
      console.error('Confirmation error:', error);
      toast.error(error.message || 'Failed to submit confirmation', { id: 'submit-confirmation' });
    } finally {
      setIsSubmittingConfirmation(false);
      toast.dismiss('verify-identity');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading wills and witness data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Error Loading Data
        </h3>
        <p className="text-gray-600 mb-4">{error.message}</p>
        <Button onClick={() => refetch()}>Try Again</Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Scroll className="h-6 w-6 mr-2 text-primary-600" />
            Will & Witness
          </h2>
          <p className="text-gray-600">
            Create and manage digital wills with cryptographic witness verification
          </p>
        </div>
        
        <Button
          variant="primary"
          icon={Plus}
          onClick={() => setShowCreateModal(true)}
        >
          Create Will
        </Button>
      </div>
      
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 overflow-x-auto">
          <button
            onClick={() => setActiveTab('created')}
            className={`
              flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap
              ${activeTab === 'created'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <Scroll size={16} />
            Wills I Created
          </button>
          <button
            onClick={() => setActiveTab('witness')}
            className={`
              flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap
              ${activeTab === 'witness'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <UserCheck size={16} />
            Witness Duties
          </button>
          <button
            onClick={() => setActiveTab('beneficiary')}
            className={`
              flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap
              ${activeTab === 'beneficiary'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <Users size={16} />
            Beneficiary Access
          </button>
        </nav>
      </div>
      
      {/* Content */}
      {filteredWills.length === 0 ? (
        <Card className="p-8 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            {activeTab === 'created' ? (
              <Scroll className="h-8 w-8 text-gray-400" />
            ) : activeTab === 'witness' ? (
              <UserCheck className="h-8 w-8 text-gray-400" />
            ) : (
              <Users className="h-8 w-8 text-gray-400" />
            )}
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {activeTab === 'created'
              ? 'No Wills Created'
              : activeTab === 'witness'
              ? 'No Witness Duties'
              : 'No Beneficiary Access'}
          </h3>
          <p className="text-gray-600 mb-6">
            {activeTab === 'created'
              ? 'You have not created any digital wills yet.'
              : activeTab === 'witness'
              ? 'You are not assigned as a witness to any wills.'
              : 'You are not a beneficiary of any wills.'}
          </p>
          {activeTab === 'created' && (
            <Button
              variant="primary"
              icon={Plus}
              onClick={() => setShowCreateModal(true)}
            >
              Create Will
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredWills.map((will) => (
            <WillCard
              key={will.id}
              will={will}
              viewType={activeTab}
              onConfirmWellness={() => handleConfirmWellness(will)}
              onViewDetails={() => handleViewWill(will)}
              onEdit={() => handleEditWill(will)}
            />
          ))}
        </div>
      )}
      
      {/* Create Will Modal */}
      <CreateWillModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onWillCreated={() => {
          setShowCreateModal(false);
          refetch();
        }}
      />
      
      {/* Witness Confirmation Modal */}
      <AnimatePresence>
        {showWitnessModal && selectedWill && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowWitnessModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md"
            >
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <UserCheck className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Witness Confirmation</h3>
                      <p className="text-sm text-gray-600">{selectedWill.title}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowWitnessModal(false)}
                    icon={X}
                  />
                </div>

                <div className="space-y-4">
                  {isConfirmingWellness && (
                    <>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-800">
                          By confirming your wellness, you are verifying that you are alive and well, and capable of fulfilling your duties as a witness. This confirmation will be recorded on the blockchain for verification.
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Confirmation Message
                        </label>
                        <textarea
                          value={confirmationMessage}
                          onChange={(e) => setConfirmationMessage(e.target.value)}
                          className="w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-20"
                          rows={3}
                          disabled={isSubmittingConfirmation}
                        />
                      </div>
                      
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-start gap-2">
                          <Wallet className="h-5 w-5 text-yellow-600 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-yellow-800">Wallet Signature Required</p>
                            <p className="text-xs text-yellow-700 mt-1">
                              You will be prompted to sign this message with your connected wallet. This creates a cryptographic proof of your confirmation.
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-3 pt-2">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setShowWitnessModal(false);
                            setIsConfirmingWellness(false);
                          }}
                          disabled={isSubmittingConfirmation}
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleSubmitConfirmation}
                          loading={isSubmittingConfirmation}
                          disabled={isSubmittingConfirmation || !confirmationMessage}
                          className="flex-1"
                        >
                          Sign & Confirm
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* View Will Modal */}
      <AnimatePresence>
        {showViewModal && selectedWill && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowViewModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            >
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                      <Scroll className="h-5 w-5 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Will Details</h3>
                      <p className="text-sm text-gray-600">{selectedWill.title}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowViewModal(false)}
                    icon={X}
                  />
                </div>

                <div className="space-y-6">
                  {/* Will Information */}
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Will Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Title</p>
                        <p className="text-sm text-gray-900">{selectedWill.title}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Created</p>
                        <p className="text-sm text-gray-900">{new Date(selectedWill.createdAt).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Status</p>
                        <p className="text-sm text-gray-900 flex items-center gap-1">
                          {selectedWill.isActive ? (
                            <>
                              <CheckCircle className="h-4 w-4 text-green-500" />
                              Active
                            </>
                          ) : (
                            <>
                              <AlertCircle className="h-4 w-4 text-red-500" />
                              Inactive
                            </>
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Creator</p>
                        <p className="text-sm text-gray-900">{selectedWill.creatorName}</p>
                      </div>
                      {selectedWill.description && (
                        <div className="md:col-span-2">
                          <p className="text-sm font-medium text-gray-700">Description</p>
                          <p className="text-sm text-gray-900">{selectedWill.description}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Witnesses */}
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Witnesses</h4>
                    <div className="bg-gray-50 rounded-lg overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Confirmation</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {selectedWill.witnesses.map((witness) => (
                            <tr key={witness.id}>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0 h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center">
                                    <User className="h-4 w-4 text-gray-500" />
                                  </div>
                                  <div className="ml-3">
                                    <p className="text-sm font-medium text-gray-900">{witness.name}</p>
                                    <p className="text-xs text-gray-500">{witness.email || 'No email'}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className="px-2 py-1 text-xs font-medium rounded-full capitalize
                                  ${witness.role === 'primary' ? 'bg-blue-100 text-blue-800' : 
                                    witness.role === 'secondary' ? 'bg-purple-100 text-purple-800' : 
                                    'bg-gray-100 text-gray-800'}">
                                  {witness.role}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                {witness.lastConfirmationAt ? 
                                  new Date(witness.lastConfirmationAt).toLocaleDateString() : 
                                  'Never'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {witness.isActive ? (
                                  <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full flex items-center w-fit">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Active
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full flex items-center w-fit">
                                    <X className="h-3 w-3 mr-1" />
                                    Inactive
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Beneficiaries */}
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Beneficiaries</h4>
                    <div className="bg-gray-50 rounded-lg overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Relationship</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Share</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Access Level</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {selectedWill.beneficiaries.map((beneficiary) => (
                            <tr key={beneficiary.id}>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0 h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center">
                                    <User className="h-4 w-4 text-gray-500" />
                                  </div>
                                  <div className="ml-3">
                                    <p className="text-sm font-medium text-gray-900">{beneficiary.name}</p>
                                    <p className="text-xs text-gray-500">{beneficiary.email || 'No email'}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                {beneficiary.relationship}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                                    <div 
                                      className="bg-primary-500 h-2 rounded-full" 
                                      style={{ width: `${beneficiary.sharePercentage}%` }}
                                    ></div>
                                  </div>
                                  <span className="text-sm text-gray-900">{beneficiary.sharePercentage}%</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className="px-2 py-1 text-xs font-medium rounded-full capitalize
                                  ${beneficiary.accessLevel === 'read' ? 'bg-blue-100 text-blue-800' : 
                                    beneficiary.accessLevel === 'download' ? 'bg-green-100 text-green-800' : 
                                    'bg-purple-100 text-purple-800'}">
                                  {beneficiary.accessLevel}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                    <Button
                      variant="outline"
                      onClick={() => setShowViewModal(false)}
                    >
                      Close
                    </Button>
                    {activeTab === 'created' && (
                      <Button
                        variant="primary"
                        icon={Edit}
                        onClick={() => {
                          setShowViewModal(false);
                          setShowEditModal(true);
                        }}
                      >
                        Edit Will
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Will Modal */}
      <AnimatePresence>
        {showEditModal && selectedWill && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowEditModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            >
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                      <Edit className="h-5 w-5 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Edit Will</h3>
                      <p className="text-sm text-gray-600">{selectedWill.title}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowEditModal(false)}
                    icon={X}
                  />
                </div>

                <div className="space-y-6">
                  {/* Edit form would go here */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-yellow-800">Will Editing Coming Soon</p>
                        <p className="text-xs text-yellow-700 mt-1">
                          The ability to edit wills is coming in a future update. This will include the ability to modify witnesses, beneficiaries, and other details.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                    <Button
                      variant="outline"
                      onClick={() => setShowEditModal(false)}
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Will Card Component
interface WillCardProps {
  will: WillDocument;
  viewType: 'created' | 'witness' | 'beneficiary';
  onConfirmWellness: () => void;
  onViewDetails: () => void;
  onEdit: () => void;
}

const WillCard: React.FC<WillCardProps> = ({ 
  will, 
  viewType, 
  onConfirmWellness,
  onViewDetails,
  onEdit
}) => {
  const { user } = useAuthStore();
  
  // Find the current user's witness or beneficiary record
  const userWitness = will.witnesses.find(w => 
    w.address === user?.walletAddress || w.email === user?.email
  );
  
  const userBeneficiary = will.beneficiaries.find(b => 
    b.address === user?.walletAddress || b.email === user?.email
  );
  
  // Format date for display
  const formatDate = (date: Date | undefined) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString();
  };
  
  return (
    <Card className="p-6 h-full flex flex-col">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center">
          <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center mr-3">
            <Scroll className="h-5 w-5 text-primary-600" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900 line-clamp-1" title={will.title}>
              {will.title}
            </h3>
            <p className="text-sm text-gray-500">
              Created {formatDate(will.createdAt)}
            </p>
          </div>
        </div>
        <div className="flex items-center">
          {will.isActive ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <CheckCircle className="h-3 w-3" />
              Active
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
              <AlertCircle className="h-3 w-3" />
              Inactive
            </span>
          )}
        </div>
      </div>
      
      {will.description && (
        <p className="text-sm text-gray-600 mb-4 line-clamp-2">
          {will.description}
        </p>
      )}
      
      {/* Creator Info */}
      {viewType !== 'created' && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Creator</h4>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <User className="h-4 w-4 text-gray-400" />
            <span>{will.creatorName}</span>
          </div>
        </div>
      )}
      
      {/* Witness Info */}
      {viewType === 'created' && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Witnesses ({will.witnesses.length})</h4>
          <div className="space-y-2">
            {will.witnesses.slice(0, 2).map((witness) => (
              <div key={witness.id} className="flex items-center gap-2 text-sm">
                <UserCheck className="h-4 w-4 text-blue-500" />
                <span className="text-gray-600">{witness.name}</span>
                {witness.lastConfirmationAt && (
                  <span className="text-xs text-green-600 ml-auto">
                    Confirmed {formatDate(witness.lastConfirmationAt)}
                  </span>
                )}
              </div>
            ))}
            {will.witnesses.length > 2 && (
              <div className="text-xs text-gray-500">
                +{will.witnesses.length - 2} more witnesses
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Beneficiary Info */}
      {viewType === 'created' && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Beneficiaries ({will.beneficiaries.length})</h4>
          <div className="space-y-2">
            {will.beneficiaries.slice(0, 2).map((beneficiary) => (
              <div key={beneficiary.id} className="flex items-center gap-2 text-sm">
                <Users className="h-4 w-4 text-purple-500" />
                <span className="text-gray-600">{beneficiary.name}</span>
                <span className="text-xs text-gray-500 ml-auto">
                  {beneficiary.sharePercentage}%
                </span>
              </div>
            ))}
            {will.beneficiaries.length > 2 && (
              <div className="text-xs text-gray-500">
                +{will.beneficiaries.length - 2} more beneficiaries
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Witness Duties */}
      {viewType === 'witness' && userWitness && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Your Witness Role</h4>
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <Shield className="h-4 w-4 text-blue-500" />
              <span className="text-gray-600 capitalize">{userWitness.role} Witness</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Clock className="h-4 w-4 text-blue-500" />
              <span className="text-gray-600">
                Last Confirmation: {formatDate(userWitness.lastConfirmationAt)}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              icon={CheckCircle}
              onClick={onConfirmWellness}
              className="w-full mt-2"
            >
              Confirm Wellness
            </Button>
          </div>
        </div>
      )}
      
      {/* Beneficiary Info */}
      {viewType === 'beneficiary' && userBeneficiary && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Your Beneficiary Status</h4>
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <Users className="h-4 w-4 text-purple-500" />
              <span className="text-gray-600">{userBeneficiary.relationship}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-500 h-2 rounded-full"
                  style={{ width: `${userBeneficiary.sharePercentage}%` }}
                />
              </div>
              <span className="text-sm font-medium text-gray-700">
                {userBeneficiary.sharePercentage}%
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Key className="h-4 w-4 text-purple-500" />
              <span className="text-gray-600 capitalize">
                {userBeneficiary.accessLevel} Access
              </span>
            </div>
          </div>
        </div>
      )}
      
      {/* Actions */}
      <div className="mt-auto pt-4 border-t border-gray-100">
        {viewType === 'created' && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              icon={Eye}
              onClick={onViewDetails}
              className="flex-1"
            >
              View
            </Button>
            <Button
              variant="outline"
              size="sm"
              icon={Edit}
              onClick={onEdit}
              className="flex-1"
            >
              Edit
            </Button>
          </div>
        )}
        
        {viewType === 'witness' && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              icon={Eye}
              onClick={onViewDetails}
              className="flex-1"
            >
              View Details
            </Button>
            <Button
              variant="primary"
              size="sm"
              icon={CheckCircle}
              onClick={onConfirmWellness}
              className="flex-1"
            >
              Confirm
            </Button>
          </div>
        )}
        
        {viewType === 'beneficiary' && (
          <Button
            variant="outline"
            size="sm"
            icon={Eye}
            onClick={onViewDetails}
            className="w-full"
          >
            View Will Details
          </Button>
        )}
      </div>
    </Card>
  );
};

// Create Will Modal Component
interface CreateWillModalProps {
  isOpen: boolean;
  onClose: () => void;
  onWillCreated: () => void;
}

const CreateWillModal: React.FC<CreateWillModalProps> = ({ isOpen, onClose, onWillCreated }) => {
  const { user } = useAuthStore();
  const { createWill, isCreatingWill } = useWills();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    documentId: '',
    witnesses: [{ address: '', name: '', email: '', role: 'primary' }],
    beneficiaries: [{ address: '', name: '', email: '', relationship: '', sharePercentage: 100 }]
  });
  const [documents, setDocuments] = useState<any[]>([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [isConnectingWallet, setIsConnectingWallet] = useState(false);

  // Load user's documents
  useEffect(() => {
    const fetchDocuments = async () => {
      setIsLoadingDocuments(true);
      try {
        const { data, error } = await supabase
          .from('documents')
          .select('*')
          .eq('user_id', user?.id)
          .order('uploaded_at', { ascending: false });
          
        if (error) {
          throw error;
        }
        
        setDocuments(data || []);
      } catch (error) {
        console.error('Failed to load documents:', error);
        toast.error('Failed to load documents');
      } finally {
        setIsLoadingDocuments(false);
      }
    };
    
    if (isOpen && user?.id) {
      fetchDocuments();
    }
  }, [isOpen, user?.id]);

  // Connect wallet
  const connectWallet = async () => {
    setIsConnectingWallet(true);
    try {
      const providers = getAvailableWalletProviders();
      
      if (providers.length === 0) {
        throw new Error('No Web3 wallets found. Please install PicaOS, MetaMask, or another compatible wallet.');
      }

      // Try PicaOS first, then fallback to other providers
      const picaosProvider = providers.find(p => p.name === 'PicaOS');
      const provider = picaosProvider || providers[0];

      toast.loading(`Connecting to ${provider.name}...`, { id: 'wallet-connect' });

      // Connect to wallet first to get the address
      const connectedAddress = await provider.connect();
      
      if (!connectedAddress) {
        throw new Error('Failed to connect to wallet');
      }

      const identity = await verifyWeb3Identity(provider, connectedAddress);
      
      if (identity.verified) {
        setWalletAddress(identity.address);
        toast.success(`${provider.name} wallet connected successfully!`, { id: 'wallet-connect' });
      } else {
        throw new Error('Identity verification failed');
      }
      
    } catch (err: any) {
      console.error('Wallet connection failed:', err);
      toast.error(`Failed to connect wallet: ${err.message}`, { id: 'wallet-connect' });
    } finally {
      setIsConnectingWallet(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleWitnessChange = (index: number, field: string, value: string) => {
    const updatedWitnesses = [...formData.witnesses];
    updatedWitnesses[index] = {
      ...updatedWitnesses[index],
      [field]: value
    };
    handleInputChange('witnesses', updatedWitnesses);
  };

  const handleBeneficiaryChange = (index: number, field: string, value: any) => {
    const updatedBeneficiaries = [...formData.beneficiaries];
    updatedBeneficiaries[index] = {
      ...updatedBeneficiaries[index],
      [field]: field === 'sharePercentage' ? parseInt(value) || 0 : value
    };
    handleInputChange('beneficiaries', updatedBeneficiaries);
  };

  const addWitness = () => {
    if (formData.witnesses.length >= 5) {
      toast.error('Maximum 5 witnesses allowed');
      return;
    }
    
    const updatedWitnesses = [
      ...formData.witnesses,
      { address: '', name: '', email: '', role: 'secondary' }
    ];
    handleInputChange('witnesses', updatedWitnesses);
  };

  const removeWitness = (index: number) => {
    if (formData.witnesses.length <= 1) {
      toast.error('At least one witness is required');
      return;
    }
    
    const updatedWitnesses = formData.witnesses.filter((_, i) => i !== index);
    handleInputChange('witnesses', updatedWitnesses);
  };

  const addBeneficiary = () => {
    if (formData.beneficiaries.length >= 10) {
      toast.error('Maximum 10 beneficiaries allowed');
      return;
    }
    
    const updatedBeneficiaries = [
      ...formData.beneficiaries,
      { address: '', name: '', email: '', relationship: '', sharePercentage: 0 }
    ];
    handleInputChange('beneficiaries', updatedBeneficiaries);
  };

  const removeBeneficiary = (index: number) => {
    if (formData.beneficiaries.length <= 1) {
      toast.error('At least one beneficiary is required');
      return;
    }
    
    const updatedBeneficiaries = formData.beneficiaries.filter((_, i) => i !== index);
    handleInputChange('beneficiaries', updatedBeneficiaries);
  };

  const validateForm = (): boolean => {
    // Basic validation
    if (!formData.title.trim()) {
      toast.error('Will title is required');
      return false;
    }
    
    if (!formData.documentId) {
      toast.error('Please select a document');
      return false;
    }
    
    if (!walletAddress) {
      toast.error('Please connect your wallet');
      return false;
    }
    
    // Validate witnesses
    for (const witness of formData.witnesses) {
      if (!witness.address.trim() || !witness.name.trim()) {
        toast.error('All witnesses must have an address and name');
        return false;
      }
    }
    
    // Validate beneficiaries
    let totalPercentage = 0;
    for (const beneficiary of formData.beneficiaries) {
      if (!beneficiary.address.trim() || !beneficiary.name.trim() || !beneficiary.relationship.trim()) {
        toast.error('All beneficiaries must have an address, name, and relationship');
        return false;
      }
      
      if (beneficiary.sharePercentage <= 0) {
        toast.error('All beneficiaries must have a share percentage greater than 0');
        return false;
      }
      
      totalPercentage += beneficiary.sharePercentage;
    }
    
    if (totalPercentage !== 100) {
      toast.error(`Total share percentage must be 100% (currently ${totalPercentage}%)`);
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      await createWill({
        title: formData.title,
        description: formData.description,
        documentId: formData.documentId,
        creatorAddress: walletAddress,
        creatorName: user?.name || 'Anonymous',
        witnesses: formData.witnesses,
        beneficiaries: formData.beneficiaries
      });
      
      toast.success('Will created successfully!');
      onWillCreated();
    } catch (error: any) {
      console.error('Failed to create will:', error);
      toast.error(error.message || 'Failed to create will');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-4xl max-h-[90vh] overflow-y-auto"
          >
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
                    <Scroll className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Create Digital Will</h3>
                    <p className="text-gray-600">Set up your digital will with witnesses and beneficiaries</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  icon={X}
                />
              </div>

              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Step 1: Basic Information */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mr-2 text-sm">1</span>
                    Basic Information
                  </h4>
                  
                  <div className="grid grid-cols-1 gap-6">
                    <Input
                      label="Will Title"
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      placeholder="e.g., Last Will and Testament"
                      required
                    />
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description (Optional)
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        placeholder="Add a description of your will..."
                        className="w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-20"
                        rows={3}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Select Document
                      </label>
                      {isLoadingDocuments ? (
                        <div className="flex items-center gap-2 text-gray-500 text-sm">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading documents...
                        </div>
                      ) : documents.length === 0 ? (
                        <div className="text-sm text-gray-500">
                          No documents found. Please upload a document first.
                        </div>
                      ) : (
                        <select
                          value={formData.documentId}
                          onChange={(e) => handleInputChange('documentId', e.target.value)}
                          className="w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-20"
                          required
                        >
                          <option value="">Select a document...</option>
                          {documents.map((doc) => (
                            <option key={doc.id} value={doc.id}>
                              {doc.name} ({new Date(doc.uploaded_at).toLocaleDateString()})
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Connect Wallet
                      </label>
                      {walletAddress ? (
                        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <CheckCircle className="h-5 w-5 text-green-500" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-green-800">Wallet Connected</p>
                            <p className="text-xs text-green-600 font-mono">{walletAddress}</p>
                          </div>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          icon={Wallet}
                          onClick={connectWallet}
                          loading={isConnectingWallet}
                          disabled={isConnectingWallet}
                          className="w-full"
                        >
                          Connect Wallet
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Step 2: Witnesses */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mr-2 text-sm">2</span>
                    Witnesses
                  </h4>
                  
                  <div className="space-y-4">
                    {formData.witnesses.map((witness, index) => (
                      <div key={index} className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="font-medium text-gray-900">
                            Witness {index + 1}
                          </h5>
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={Trash2}
                            onClick={() => removeWitness(index)}
                            disabled={formData.witnesses.length <= 1}
                            className="text-red-500 hover:text-red-700"
                          />
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <Input
                            label="Name"
                            type="text"
                            value={witness.name}
                            onChange={(e) => handleWitnessChange(index, 'name', e.target.value)}
                            placeholder="Witness name"
                            required
                          />
                          
                          <Input
                            label="Wallet Address"
                            type="text"
                            value={witness.address}
                            onChange={(e) => handleWitnessChange(index, 'address', e.target.value)}
                            placeholder="0x..."
                            required
                          />
                          
                          <Input
                            label="Email (Optional)"
                            type="email"
                            value={witness.email}
                            onChange={(e) => handleWitnessChange(index, 'email', e.target.value)}
                            placeholder="witness@example.com"
                          />
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Role
                            </label>
                            <select
                              value={witness.role}
                              onChange={(e) => handleWitnessChange(index, 'role', e.target.value)}
                              className="w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-20"
                              required
                            >
                              <option value="primary">Primary</option>
                              <option value="secondary">Secondary</option>
                              <option value="backup">Backup</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    <Button
                      variant="outline"
                      size="sm"
                      icon={Plus}
                      onClick={addWitness}
                      disabled={formData.witnesses.length >= 5}
                      className="w-full"
                    >
                      Add Witness
                    </Button>
                  </div>
                </div>
                
                {/* Step 3: Beneficiaries */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mr-2 text-sm">3</span>
                    Beneficiaries
                  </h4>
                  
                  <div className="space-y-4">
                    {formData.beneficiaries.map((beneficiary, index) => (
                      <div key={index} className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="font-medium text-gray-900">
                            Beneficiary {index + 1}
                          </h5>
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={Trash2}
                            onClick={() => removeBeneficiary(index)}
                            disabled={formData.beneficiaries.length <= 1}
                            className="text-red-500 hover:text-red-700"
                          />
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <Input
                            label="Name"
                            type="text"
                            value={beneficiary.name}
                            onChange={(e) => handleBeneficiaryChange(index, 'name', e.target.value)}
                            placeholder="Beneficiary name"
                            required
                          />
                          
                          <Input
                            label="Wallet Address"
                            type="text"
                            value={beneficiary.address}
                            onChange={(e) => handleBeneficiaryChange(index, 'address', e.target.value)}
                            placeholder="0x..."
                            required
                          />
                          
                          <Input
                            label="Email (Optional)"
                            type="email"
                            value={beneficiary.email}
                            onChange={(e) => handleBeneficiaryChange(index, 'email', e.target.value)}
                            placeholder="beneficiary@example.com"
                          />
                          
                          <Input
                            label="Relationship"
                            type="text"
                            value={beneficiary.relationship}
                            onChange={(e) => handleBeneficiaryChange(index, 'relationship', e.target.value)}
                            placeholder="e.g., Spouse, Child, Friend"
                            required
                          />
                          
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Share Percentage
                            </label>
                            <div className="flex items-center gap-3">
                              <input
                                type="range"
                                min="1"
                                max="100"
                                value={beneficiary.sharePercentage}
                                onChange={(e) => handleBeneficiaryChange(index, 'sharePercentage', e.target.value)}
                                className="flex-1"
                              />
                              <div className="w-16">
                                <Input
                                  type="number"
                                  value={beneficiary.sharePercentage.toString()}
                                  onChange={(e) => handleBeneficiaryChange(index, 'sharePercentage', e.target.value)}
                                  min="1"
                                  max="100"
                                  required
                                />
                              </div>
                              <span className="text-sm text-gray-500">%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    <Button
                      variant="outline"
                      size="sm"
                      icon={Plus}
                      onClick={addBeneficiary}
                      disabled={formData.beneficiaries.length >= 10}
                      className="w-full"
                    >
                      Add Beneficiary
                    </Button>
                  </div>
                </div>
                
                {/* Submit */}
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      type="button"
                      onClick={onClose}
                      disabled={isCreatingWill}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      type="submit"
                      loading={isCreatingWill}
                      disabled={isCreatingWill}
                      className="flex-1"
                    >
                      Create Will
                    </Button>
                  </div>
                </div>
              </form>
            </Card>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WillWitnessPage;